import { FormActionType } from 'utils/api/modules/entities/entities.types';

import { EntitySubmissionDraftType } from './EntitiesFormSubmission';

/**
 * Determines the next section in a form based on current answers and form configuration.
 * This is an offline version that doesn't make API calls.
 *
 * @param data - The current form submission data
 * @returns Object containing the updated stack and whether to show preview
 */

export function determineNextSection(data: EntitySubmissionDraftType): {
  stack: string[];
  showPreview: boolean;
} {
  const { stack, form, content } = data;
  const currentSection = stack[stack.length - 1];
  const currentSectionProperties = form.form.properties[currentSection];
  const actions = (currentSectionProperties.actions || []) as FormActionType[];

  // check if all required questions from section are filled
  const { required } = currentSectionProperties;

  if (!required.every((q) => content[q] != null && content[q] !== '')) {
    return { stack, showPreview: false };
  }

  // STEP 1: Try to match based on field values
  for (const action of actions) {
    if (typeof action.when === 'object' && 'equals' in action.when) {
      const [questionKey, expectedValues] = action.when.equals;
      const actualAnswers = (content[questionKey] || []) as string[];

      const match = expectedValues.some((val) => actualAnswers.includes(val));

      if (match) {
        if ('nextSection' in action.then) {
          return { stack: [...stack, action.then.nextSection], showPreview: false };
        }
        if ('submit' in action.then) {
          return { stack, showPreview: true };
        }
      }
    }
  }

  // STEP 2: No field match – look for default action (when: true)
  const defaultAction = actions.find((action) => typeof action.when === 'boolean' && action.when === true);

  if (defaultAction) {
    if ('nextSection' in defaultAction.then) {
      return { stack: [...stack, defaultAction.then.nextSection], showPreview: false };
    }
    if ('submit' in defaultAction.then) {
      return { stack, showPreview: true };
    }
  }

  // STEP 3: No matching action at all – use default form order
  const currentIndex = form.form.order.indexOf(currentSection);
  const nextSection = form.form.order[currentIndex + 1];

  if (nextSection) {
    return { stack: [...stack, nextSection], showPreview: false };
  }

  return { stack, showPreview: true };
}

export function allRequiredFieldsFilled(data: EntitySubmissionDraftType): boolean {
  const { required: requiredFields } = data.form.form.properties[data.stack[data.stack.length - 1]];

  return requiredFields.every((q) => data.content[q] != null && data.content[q] !== '');
}

export function allRequiredFieldsForSection(data: EntitySubmissionDraftType, section: string): string[] {
  const { required: requiredFields } = data.form.form.properties[section];

  return requiredFields;
}

export function fieldIsRequired(data: EntitySubmissionDraftType, section: string, field: string): boolean {
  return allRequiredFieldsForSection(data, section).includes(field);
}

export function blankFields(data: EntitySubmissionDraftType): string[] {
  return Object.keys(data.content).filter((field) => data.content[field] === null || data.content[field] === '');
}

export function invalidFields(data: EntitySubmissionDraftType): string[] {
  return allRequiredFieldsForSection(data, data.stack[data.stack.length - 1]).filter((field) => {
    const value = data.content[field];

    return value === null || value === '' || value === undefined;
  });
}
